/**
 * React hooks for research-service data fetching
 * Provides loading states, error handling, caching, and manual refresh
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type {
  EntityType,
  ResearchRequestParams,
  GapRequestParams,
  ResearchResponse,
  GapResponse,
  ResearchQueryState,
} from '../types/research-service';
import { researchService } from '../services/research-service';

// ============================================================================
// Cache Configuration
// ============================================================================

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const cache = new Map<string, CacheEntry<any>>();

function getCacheKey(prefix: string, params: object): string {
  return `${prefix}:${JSON.stringify(params)}`;
}

function getFromCache<T>(key: string): T | null {
  const entry = cache.get(key);
  if (!entry) return null;

  const isExpired = Date.now() - entry.timestamp > CACHE_TTL;
  if (isExpired) {
    cache.delete(key);
    return null;
  }

  return entry.data as T;
}

function setCache<T>(key: string, data: T): void {
  cache.set(key, { data, timestamp: Date.now() });
}

// ============================================================================
// Generic Research Data Hook
// ============================================================================

function useResearchQuery<T>(
  fetchFn: () => Promise<T>,
  cacheKey: string,
  enabled = true
): ResearchQueryState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const isMounted = useRef(true);

  const fetchData = useCallback(async () => {
    if (!enabled) return;

    // Check cache first
    const cached = getFromCache<T>(cacheKey);
    if (cached) {
      setData(cached);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await fetchFn();
      if (isMounted.current) {
        setData(result);
        setCache(cacheKey, result);
        setError(null);
      }
    } catch (err) {
      if (isMounted.current) {
        setError(err instanceof Error ? err : new Error('Unknown error'));
        setData(null);
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  }, [fetchFn, cacheKey, enabled]);

  useEffect(() => {
    isMounted.current = true;
    fetchData();

    return () => {
      isMounted.current = false;
    };
  }, [fetchData]);

  const refetch = useCallback(async () => {
    cache.delete(cacheKey); // Clear cache on manual refetch
    await fetchData();
  }, [cacheKey, fetchData]);

  return { data, loading, error, refetch };
}

// ============================================================================
// Specific Entity Hooks
// ============================================================================

/**
 * Hook for fetching topics
 */
export function useTopics(
  params: ResearchRequestParams,
  enabled = true
): ResearchQueryState<ResearchResponse> {
  const fetchFn = useCallback(
    () => researchService.getTopics(params),
    [params.dimension, params.value, params.limit]
  );
  const cacheKey = getCacheKey('topics', params);

  return useResearchQuery(fetchFn, cacheKey, enabled);
}

/**
 * Hook for fetching opinions
 */
export function useOpinions(
  params: ResearchRequestParams,
  enabled = true
): ResearchQueryState<ResearchResponse> {
  const fetchFn = useCallback(
    () => researchService.getOpinions(params),
    [params.dimension, params.value, params.limit]
  );
  const cacheKey = getCacheKey('opinions', params);

  return useResearchQuery(fetchFn, cacheKey, enabled);
}

/**
 * Hook for fetching questions
 */
export function useQuestions(
  params: ResearchRequestParams,
  enabled = true
): ResearchQueryState<ResearchResponse> {
  const fetchFn = useCallback(
    () => researchService.getQuestions(params),
    [params.dimension, params.value, params.limit]
  );
  const cacheKey = getCacheKey('questions', params);

  return useResearchQuery(fetchFn, cacheKey, enabled);
}

/**
 * Hook for fetching categories
 */
export function useCategories(
  params: ResearchRequestParams,
  enabled = true
): ResearchQueryState<ResearchResponse> {
  const fetchFn = useCallback(
    () => researchService.getCategories(params),
    [params.dimension, params.value, params.limit]
  );
  const cacheKey = getCacheKey('categories', params);

  return useResearchQuery(fetchFn, cacheKey, enabled);
}

/**
 * Hook for fetching products
 */
export function useProducts(
  params: ResearchRequestParams,
  enabled = true
): ResearchQueryState<ResearchResponse> {
  const fetchFn = useCallback(
    () => researchService.getProducts(params),
    [params.dimension, params.value, params.limit]
  );
  const cacheKey = getCacheKey('products', params);

  return useResearchQuery(fetchFn, cacheKey, enabled);
}

/**
 * Hook for fetching threads
 */
export function useThreads(
  params: ResearchRequestParams,
  enabled = true
): ResearchQueryState<ResearchResponse> {
  const fetchFn = useCallback(
    () => researchService.getThreads(params),
    [params.dimension, params.value, params.limit]
  );
  const cacheKey = getCacheKey('threads', params);

  return useResearchQuery(fetchFn, cacheKey, enabled);
}

// ============================================================================
// Gap Analysis Hooks
// ============================================================================

/**
 * Hook for fetching topic gaps
 */
export function useTopicGaps(
  params: GapRequestParams,
  enabled = true
): ResearchQueryState<GapResponse> {
  const fetchFn = useCallback(
    () => researchService.getTopicGaps(params),
    [params.dimension, params.value, params.customerId]
  );
  const cacheKey = getCacheKey('topic-gaps', params);

  return useResearchQuery(fetchFn, cacheKey, enabled);
}

/**
 * Hook for fetching question gaps
 */
export function useQuestionGaps(
  params: GapRequestParams,
  enabled = true
): ResearchQueryState<GapResponse> {
  const fetchFn = useCallback(
    () => researchService.getQuestionGaps(params),
    [params.dimension, params.value, params.customerId]
  );
  const cacheKey = getCacheKey('question-gaps', params);

  return useResearchQuery(fetchFn, cacheKey, enabled);
}

// ============================================================================
// Generic Hook (for advanced use cases)
// ============================================================================

/**
 * Generic hook for any research entity type
 */
export function useResearchEntity(
  entity: EntityType,
  params: ResearchRequestParams,
  enabled = true
): ResearchQueryState<ResearchResponse> {
  const fetchFn = useCallback(() => {
    switch (entity) {
      case 'topics':
        return researchService.getTopics(params);
      case 'opinions':
        return researchService.getOpinions(params);
      case 'questions':
        return researchService.getQuestions(params);
      case 'categories':
        return researchService.getCategories(params);
      case 'products':
        return researchService.getProducts(params);
      case 'threads':
        return researchService.getThreads(params);
      default:
        throw new Error(`Unknown entity type: ${entity}`);
    }
  }, [entity, params.dimension, params.value, params.limit]);

  const cacheKey = getCacheKey(entity, params);

  return useResearchQuery(fetchFn, cacheKey, enabled);
}
